import { ZERO_WEI, PERIOD_IN_SECONDS, Period } from '@kwenta/sdk/constants'
import {
	TransactionStatus,
	PositionSide,
	FuturesMarginType,
	FuturesMarket,
	PerpsV2PositionHistory,
} from '@kwenta/sdk/types'
import {
	truncateTimestamp,
	getDisplayAsset,
	getDefaultPriceImpact,
	calculateDesiredFillPrice,
	toSnakeCase,
} from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { createSelector } from '@reduxjs/toolkit'
import { Address } from 'viem'

import { HistoryTab } from 'sections/dashboard/History/HistoryTabs'
import { selectEthWalletBalance } from 'state/balances/selectors'
import {
	selectCrossMarginAccountTrades,
	selectCrossMarginAccountData,
	selectCrossMarginAvailableMargin,
	selectCrossMarginMarginTransfers,
	selectCrossMarginMaxLeverage,
	selectSelectedCrossMarginPosition,
	selectCrossMarginPositionHistory,
	selectCrossMarginPositions,
	selectMarkPriceInfosV3,
	selectMarkPricesV3,
	selectPendingAsyncOrdersCount,
	selectV3SelectedMarketInfo,
	selectV3MarketKey,
	selectV3Markets,
	selectV3SkewAdjustedPrice,
	selectV3SkewAdjustedPriceInfo,
	selectSignerSupportsCrossMargin,
	selectCrossMarginAccountDataBySignerWallet,
	selectV3EditPositionModalInfo,
	selectCMEditPreviewUnserialized,
	selectCrossMarginOrderTableItems,
	selectTradePanelKeeperDepositV3,
	selectSLTPModalUsdDepositV3,
	selectIsFetchingCMTradePreview,
	selectMaxUsdSizeInputCrossMargin,
	selectShowSwitchNetworkCrossMargin,
	selectCrossMarginAccountTransfers,
	selectKeeperUsdDepositExceedsBal,
	selectCloseCMPositionPreview,
	selectCloseModalKeeperDepositV3,
	selectPerpsV3MarketVolumes,
	selectCrossMarginAccountOrderHistory,
	selectPositionHistoryV3ForSelectedTrader,
	selectV3ChartData,
	selectCrossMarginAccountLiquidations,
	selectCrossMarginActivePositions,
} from 'state/futures/snxPerpsV3/selectors'
import { selectCrossMarginAccount } from 'state/futures/snxPerpsV3/selectors'
import { selectPrices } from 'state/prices/selectors'
import { RootState } from 'state/store'
import { FetchStatus } from 'state/types'
import {
	selectIsSubAccountMode,
	selectProviderNetwork,
	selectSignerWallet,
	selectWallet,
} from 'state/wallet/selectors'
import {
	unserializeTrades,
	stopLossValidity,
	takeProfitValidity,
	formatPositionForTable,
	unserializePerpsV2PositionHistory,
	shouldFilterUserInfoTable,
	shouldFilterHistoryTableByDate,
	shouldFilterHistoryTable,
} from 'utils/futures'

import {
	selectEditPositionInputs,
	selectPerpsProviderState,
	selectPerpsProvider,
	selectMarketAsset,
	selectMarketIndexPrice,
	selectMarketOnchainPrice,
	selectOneClickTradingSelected,
	selectSelectedPortfolioTimeframe,
	selectTradingMode,
	selectUserInfoShowAllMarkets,
	selectRawTradePanelInputs,
	selectTradePanelInputs,
	selectLeverageSide,
	selectLeverageInput,
	selectSlTpTradeInputs,
} from './common/selectors'
import {
	selectAccountMarginTransfers,
	selectMarginDeltaInputValue,
	selectSmartMarginDelayedOrders,
	selectV2Markets,
	selectSmartMarginAccount,
	selectSmartMarginAccountData,
	selectSmartMarginTradePreview,
	selectV2MarketKey,
	selectSmartMarginMaxLeverage,
	selectV2EditPositionModalInfo,
	selectMarkPricesV2,
	selectMarkPriceInfosV2,
	selectSmartMarginActivePositions,
	selectAllSmartMarginTrades,
	selectV2MarketInfo,
	selectSelectedSmartMarginPosition,
	selectV2SkewAdjustedPrice,
	selectV2SkewAdjustedPriceInfo,
	selectIdleAccountMargin,
	selectWalletChainSupportsSmartMargin,
	selectKeeperDepositExceedsAbstractionBal,
	selectSmartMarginAccountDataBySignerWallet,
	selectSmartMarginKeeperDeposit,
	selectIsFetchingTradePreviewSmartMargin,
	selectSMEditPreviewUnserialized,
	selectAllSmartMarginConditionalOrders,
	selectShowSwitchNetworkSmartMargin,
	selectSmartMarginAccountTransfers,
	selectCloseSMPositionPreview,
	selectPerpsV2MarketVolumes,
	selectSmartMarginAccountOrderHistory,
	selectPositionHistoryV2ForSelectedTrader,
	selectConditionalOrdersForV2Market,
} from './snxPerpsV2/selectors'
import { PerpsV2Portfolio } from './snxPerpsV2/types'
import { PerpsProvider, PortfolioValues, TableFilteredFuturesTabs, TradingModes } from './types'

export const selectQueryStatuses = createSelector(
	selectPerpsProviderState,
	(selectedFuturesState) => ({
		...selectedFuturesState.queryStatuses,
	})
)

export const selectMarketsQueryStatus = createSelector(
	selectQueryStatuses,
	(statuses) => statuses.markets
)

export const selectSelectedTrader = (state: RootState) => state.futures.leaderboard.selectedTrader

export const selectLeaderboardPageSize = (state: RootState) => state.futures.leaderboard.pageSize

export const selectShowHistory = (state: RootState) => !!state.futures.preferences.showHistory

export const selectShowOrderLines = (state: RootState) => !!state.futures.preferences.showOrderLines

export const selectShowPositionLines = (state: RootState) =>
	!!state.futures.preferences.showPositionLines

export const selectShowRealizedPnL = (state: RootState) =>
	!!state.futures.preferences.showRealizedPnL

export const selectShowTestnets = (state: RootState) => !!state.futures.preferences.showTestnets

export const selectShowFunding = (state: RootState) => !!state.futures.preferences.showFunding

export const selectAccountData = createSelector(
	selectPerpsProvider,
	selectSmartMarginAccountData,
	selectCrossMarginAccountData,
	(type, smartAccountData, crossAccountData) =>
		type === PerpsProvider.SNX_V2 ? smartAccountData : crossAccountData
)

export const selectMarkets = createSelector(
	selectPerpsProvider,
	selectV2Markets,
	selectV3Markets,
	(futuresType, v2Markets, v3Markets) => {
		return futuresType === PerpsProvider.SNX_V3 ? v3Markets : v2Markets
	}
)

export const selectMarketsLength = createSelector(selectMarkets, (markets) => markets.length)

export const selectMarketVolumes = createSelector(
	selectPerpsProvider,
	selectPerpsV2MarketVolumes,
	selectPerpsV3MarketVolumes,
	(type, v2Volume, v3Volume) => {
		return type === PerpsProvider.SNX_V3 ? v3Volume : v2Volume
	}
)

export const selectMarketKey = createSelector(
	selectPerpsProvider,
	selectV2MarketKey,
	selectV3MarketKey,
	(type, v2Key, v3Key) => (type === PerpsProvider.SNX_V3 ? v3Key : v2Key)
)

export const selectMarketInfo = createSelector(
	selectPerpsProvider,
	selectV2MarketInfo,
	selectV3SelectedMarketInfo,
	(type, v2Key, v3Key) => (type === PerpsProvider.SNX_V3 ? v3Key : v2Key)
)

export const selectMarketPrices = createSelector(
	selectMarketAsset,
	selectPrices,
	(marketAsset, prices) => {
		return prices[marketAsset] ?? {}
	}
)

export const selectMarkPrices = createSelector(
	selectMarkPricesV2,
	selectMarkPricesV3,
	selectPerpsProvider,
	(v2, v3, type) => {
		return type === PerpsProvider.SNX_V3 ? v3 : v2
	}
)

export const selectMarkPriceInfos = createSelector(
	selectMarkPriceInfosV2,
	selectMarkPriceInfosV3,
	selectPerpsProvider,
	(v2, v3, type) => {
		return type === PerpsProvider.SNX_V3 ? v3 : v2
	}
)

export const selectFuturesAccount = createSelector(
	selectPerpsProvider,
	selectWallet,
	selectSmartMarginAccount,
	(selectedType, wallet, smartMarginAccount) => {
		return selectedType === PerpsProvider.SNX_V2 ? smartMarginAccount : wallet
	}
)

export const selectPosition = createSelector(
	selectSelectedSmartMarginPosition,
	selectSelectedCrossMarginPosition,
	selectPerpsProvider,
	(v2, v3, type) => {
		return type === PerpsProvider.SNX_V3 ? v3 : v2
	}
)

export const selectSkewAdjustedPrice = createSelector(
	selectV2SkewAdjustedPrice,
	selectV3SkewAdjustedPrice,
	selectPerpsProvider,
	(v2, v3, type) => {
		return type === PerpsProvider.SNX_V3 ? v3 : v2
	}
)

export const selectSkewAdjustedPriceInfo = createSelector(
	selectV2SkewAdjustedPriceInfo,
	selectV3SkewAdjustedPriceInfo,
	selectPerpsProvider,
	(v2, v3, type) => {
		return type === PerpsProvider.SNX_V3 ? v3 : v2
	}
)

export const selectV2PositionHistory = createSelector(selectSmartMarginAccountData, (accountData) =>
	unserializePerpsV2PositionHistory(accountData?.positionHistory ?? [])
)

export const selectPositionHistory = createSelector(
	selectPerpsProvider,
	selectV2PositionHistory,
	selectCrossMarginPositionHistory,
	(type, v2History, v3History) => {
		if (type === PerpsProvider.SNX_V3) {
			return v3History
		} else {
			return v2History
		}
	}
)

export const selectSelectedMarketPositionHistory = createSelector(
	selectMarketAsset,
	selectPositionHistory,
	(marketAsset, positionHistory) => {
		return (positionHistory as PerpsV2PositionHistory[]).find(
			({ asset, isOpen }) => isOpen && asset === marketAsset
		)
	}
)

export const selectPositionHistoryForSelectedTrader = createSelector(
	selectPerpsProvider,
	selectPositionHistoryV2ForSelectedTrader,
	selectPositionHistoryV3ForSelectedTrader,
	(type, v2History, v3History) => {
		return type === PerpsProvider.SNX_V3 ? v3History : v2History
	}
)

export const selectFuturesPositions = createSelector(
	selectSmartMarginActivePositions,
	selectCrossMarginPositions,
	(state: RootState) => state.futures.preferences.selectedProvider,
	(smartPositions, crossPositions, selectedProvider) => {
		return selectedProvider === PerpsProvider.SNX_V3 ? crossPositions : smartPositions
	}
)

export const selectActivePositions = createSelector(
	selectSmartMarginActivePositions,
	selectCrossMarginActivePositions,
	(state: RootState) => state.futures.preferences.selectedProvider,
	(smartPositions, crossPositions, selectedProvider) => {
		return selectedProvider === PerpsProvider.SNX_V3 ? crossPositions : smartPositions
	}
)

export const selectUsersPositionHistory = createSelector(
	selectProviderNetwork,
	selectWallet,
	selectPerpsProvider,
	selectCrossMarginPositionHistory,
	(state: RootState) => state.snxPerpsV2,
	(networkId, wallet, type, cmHistory, futures) => {
		if (type === PerpsProvider.SNX_V3) return cmHistory
		if (!wallet) return []
		const history = futures.leaderboard.selectedTraderV2PositionHistory[networkId]?.[wallet] ?? []
		return unserializePerpsV2PositionHistory(history)
	}
)

export const selectTotalUnrealizedPnl = createSelector(selectFuturesPositions, (positions) => {
	return positions.reduce((acc, p) => {
		return acc.add(p?.details?.pnl ?? ZERO_WEI)
	}, ZERO_WEI)
})

export const selectSubmittingFuturesTx = createSelector(
	(state: RootState) => state.app,
	(app) => {
		return (
			app.transaction?.status === TransactionStatus.AwaitingExecution ||
			app.transaction?.status === TransactionStatus.Executed
		)
	}
)

export const selectIsClosingPosition = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return (
			(app.transaction?.type === 'close_smart_margin' ||
				app.transaction?.type === 'close_cross_margin') &&
			submitting
		)
	}
)

export const selectIsSubmittingCrossTransfer = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return (
			(app.transaction?.type === 'deposit_smart_margin' ||
				app.transaction?.type === 'withdraw_smart_margin') &&
			submitting
		)
	}
)

export const selectIsApprovingCrossDeposit = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return app.transaction?.type === 'approve_cross_margin' && submitting
	}
)

export const selectIsSubmittingIsolatedTransfer = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return (
			(app.transaction?.type === 'deposit_cross_margin' ||
				app.transaction?.type === 'withdraw_cross_margin' ||
				app.transaction?.type === 'approve_cross_margin') &&
			submitting
		)
	}
)

export const selectCrossMarginTransferError = createSelector(
	(state: RootState) => state.app,
	(app) => {
		return (app.transaction?.type === 'deposit_cross_margin' ||
			app.transaction?.type === 'withdraw_cross_margin') &&
			app.transaction?.status === TransactionStatus.Failed
			? app.transaction?.error ?? 'Transaction failed'
			: null
	}
)

export const selectIsCancellingOrder = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return app.transaction?.type === 'cancel_delayed_isolated' && submitting
	}
)

export const selectIsExecutingOrder = createSelector(
	selectSubmittingFuturesTx,
	(state: RootState) => state.app,
	(submitting, app) => {
		return app.transaction?.type === 'execute_delayed_isolated' && submitting
	}
)

export const selectMaxLeverage = createSelector(
	selectCrossMarginMaxLeverage,
	selectSmartMarginMaxLeverage,
	selectPerpsProvider,
	(cmMax, smMax, type) => {
		return type === PerpsProvider.SNX_V3 ? cmMax : smMax
	}
)

export const selectSelectedInputDenomination = (state: RootState) =>
	state.futures.preferences.selectedInputDenomination

export const selectSelectedInputHours = (state: RootState) =>
	state.futures.preferences.selectedInputHours

export const selectNewTradeHasSlTp = createSelector(
	selectTradePanelInputs,
	(tradeInputs) => tradeInputs.stopLossPrice?.gt(0) || tradeInputs.takeProfitPrice?.gt(0)
)

export const selectTradePrice = createSelector(
	selectRawTradePanelInputs,
	selectMarketIndexPrice,
	(inputs, indexPrice) => {
		return wei(inputs.orderPrice.price || indexPrice)
	}
)

export const selectTradeInputPriceString = createSelector(selectRawTradePanelInputs, (inputs) => {
	return inputs.orderPrice.price ?? ''
})

export const selectTradeSizeInputsDisabled = createSelector(
	selectMarginDeltaInputValue,
	selectPerpsProvider,
	selectCrossMarginAvailableMargin,
	(marginDeltaInput, perpsProvider, availableMargin) => {
		const remaining =
			perpsProvider === PerpsProvider.SNX_V3
				? availableMargin || ZERO_WEI
				: wei(marginDeltaInput || 0)
		return remaining.lte(0)
	}
)

export const selectNextPriceDisclaimer = createSelector(
	selectMaxLeverage,
	selectLeverageInput,
	(maxLeverage, leverage) => {
		return wei(leverage || 0).gte(maxLeverage.sub(wei(1))) && wei(leverage || 0).lte(maxLeverage)
	}
)

export const selectFuturesPortfolio = createSelector(
	selectSmartMarginActivePositions,
	selectCrossMarginPositions,
	selectIdleAccountMargin,
	(smartPositions, crossPositions, idleMargin) => {
		// TODO: Update this for cross margin
		const crossValue =
			crossPositions.reduce((sum, { remainingMargin }) => sum.add(remainingMargin ?? 0), wei(0)) ??
			wei(0)
		const smartValue =
			smartPositions.reduce((sum, { remainingMargin }) => sum.add(remainingMargin), wei(0)) ??
			wei(0)
		const totalValue = smartValue.add(crossValue).add(idleMargin)

		return {
			total: totalValue,
			smartMargin: smartValue.add(idleMargin),
			crossMargin: crossValue,
		}
	}
)

export const selectSmartMarginTransfers = createSelector(
	selectSmartMarginAccountData,
	(account) => {
		return account?.marketMarginTransfers ?? []
	}
)

export const selectMarginTransfers = createSelector(
	selectPerpsProvider,
	selectCrossMarginMarginTransfers,
	selectSmartMarginTransfers,
	(state: RootState) => state.futures,
	(type, smTransfers, cmTransfers) => {
		return type === PerpsProvider.SNX_V3 ? cmTransfers : smTransfers
	}
)

export const selectMarketMarginTransfers = createSelector(
	selectMarginTransfers,
	selectMarketAsset,
	(state: RootState) => state.futures,
	(transfers, asset) => {
		return transfers.filter((o) => o.asset === asset)
	}
)

export const selectPendingDelayedOrder = createSelector(
	selectSmartMarginDelayedOrders,
	selectMarketKey,
	(delayedOrders, marketKey) => {
		return delayedOrders.find((o) => o.market.marketKey === marketKey)
	}
)

export const selectOpenInterest = createSelector(selectMarkets, (futuresMarkets) =>
	(futuresMarkets as FuturesMarket[]).reduce(
		(total, { openInterest }) => total.add(openInterest.shortUSD).add(openInterest.longUSD),
		wei(0)
	)
)

export const selectAllUsersTrades = createSelector(
	selectCrossMarginAccountTrades,
	selectSmartMarginAccountData,
	(crossAccountTrades, smartAccountData) => {
		// Normalize data across margin perps types
		const crossTrades = crossAccountTrades.map((t) => ({
			...t,
			asset: t.market?.asset,
			size: t.sizeDelta.abs(),
			feesPaid: t.totalFees,
			pnl: wei(0),
			orderType: 'market',
			side: t.sizeDelta.lt(0) ? PositionSide.SHORT : PositionSide.LONG,
			price: t.fillPrice,
			positionClosed: false,
		}))
		const allTrades = [...crossTrades, ...unserializeTrades(smartAccountData?.trades ?? [])]
		return allTrades
	}
)

export const selectAllTradesForAccountType = createSelector(
	selectCrossMarginAccountTrades,
	selectAllSmartMarginTrades,
	selectPerpsProvider,
	(crossMarginTrades, smartMarginTrades, perpsProvider) => {
		return perpsProvider === PerpsProvider.SNX_V3 ? crossMarginTrades : smartMarginTrades
	}
)

export const selectCrossMarginPortfolioValues = createSelector(selectFuturesPortfolio, () => {
	// TODO: Calc cross margin portfolio values
	return [
		{
			timestamp: Date.now(),
			total: 0,
		},
	]
})

export const selectSmartMarginPortfolioValues = createSelector(
	selectAllSmartMarginTrades,
	selectSmartMarginTransfers,
	selectAccountMarginTransfers,
	selectFuturesPortfolio,
	(trades, transfers, accountTransfers, portfolioTotal) => {
		const tradeActions = trades.map(({ account, timestamp, asset, margin }) => ({
			account,
			timestamp,
			asset,
			margin: margin.toNumber(),
			size: 0,
			type: 'trade',
		}))

		const transferActions = transfers.map(({ account, timestamp, asset, size }) => ({
			account,
			timestamp,
			asset,
			size,
			margin: 0,
			type: 'market_transfer',
		}))

		const accountTransferActions = accountTransfers.map(({ account, timestamp, asset, size }) => ({
			account,
			timestamp,
			asset,
			size,
			margin: 0,
			type: 'account_transfer',
		}))

		const actions = [...tradeActions, ...transferActions, ...accountTransferActions]
			.filter((action) => !!action)
			.sort((a, b) => {
				if (a.timestamp === b.timestamp) return a.type === 'account_transfer' ? -1 : 1
				return a.timestamp - b.timestamp
			})

		const accountHistory = actions.reduce((acc, action) => {
			if (acc.length === 0) {
				const newTotal = action.size !== 0 ? action.size : action.margin
				const lastAction =
					action.type === 'account_transfer' || !action.asset
						? {
								account: action.account,
								timestamp: action.timestamp,
								assets: {},
								idle: newTotal,
								total: newTotal,
						  }
						: {
								account: action.account,
								timestamp: action.timestamp,
								assets: {
									[action.asset]: newTotal,
								},
								idle: 0,
								total: newTotal,
						  }

				return [lastAction]
			} else {
				const lastAction = acc[acc.length - 1]
				const newAssets = !action.asset
					? lastAction.assets
					: {
							...lastAction.assets,
							[action.asset]:
								action.size !== 0
									? (lastAction.assets[action.asset] ?? 0) + action.size
									: action.margin,
					  }
				const newIdle = !action.asset
					? lastAction.idle + action.size
					: lastAction.idle + action.size * -1

				const newTotal =
					Object.entries(newAssets).reduce((acc, asset) => acc + asset[1], 0) + lastAction.idle

				const newAction = {
					...lastAction,
					timestamp: action.timestamp,
					assets: newAssets,
					idle: newIdle,
					total: newTotal,
				}
				const replacePrevious = newAction.timestamp === lastAction.timestamp

				return [...acc.slice(0, acc.length - (replacePrevious ? 1 : 0)), newAction]
			}
		}, [] as PerpsV2Portfolio[])
		return [
			...accountHistory.map(({ timestamp, total }) => ({ timestamp: timestamp * 1000, total })),
			{
				timestamp: Date.now(),
				total: portfolioTotal.smartMargin.toNumber(),
			},
		]
	}
)

const INTERPOLATION_GAPS: Partial<Record<Period, number>> = {
	[Period.ONE_WEEK]: PERIOD_IN_SECONDS[Period.ONE_HOUR],
	[Period.ONE_MONTH]: PERIOD_IN_SECONDS[Period.ONE_HOUR] * 6,
	[Period.ONE_YEAR]: PERIOD_IN_SECONDS[Period.ONE_DAY],
}

export const selectPortfolioChartData = createSelector(
	selectV3ChartData,
	selectSmartMarginPortfolioValues,
	selectSelectedPortfolioTimeframe,
	(crossMarginPnlValues, smartPortfolioValues, timeframe) => {
		// get the timeframe for interpolation
		const interpolationGap = INTERPOLATION_GAPS[timeframe] ?? PERIOD_IN_SECONDS[Period.ONE_WEEK]

		const minTimestamp = Date.now() - PERIOD_IN_SECONDS[timeframe] * 1000

		const createPortfolioData = (portfolioValues: PortfolioValues[]) => {
			if (portfolioValues.length === 0) return []
			const filteredPortfolioValues = portfolioValues.filter(
				({ timestamp }) => timestamp >= minTimestamp
			)

			const portfolioData: PortfolioValues[] = []
			const totalCount = filteredPortfolioValues.length
				? filteredPortfolioValues.length > 1
					? filteredPortfolioValues.length
					: 2
				: 0
			for (let i = 0; i < totalCount; i++) {
				if (i < totalCount - 1) {
					const index = filteredPortfolioValues.length > 1 ? i : 0
					const currentTimestamp = truncateTimestamp(
						filteredPortfolioValues[index].timestamp,
						interpolationGap * 1000
					)
					const nextTimestamp = truncateTimestamp(
						filteredPortfolioValues[index + 1]?.timestamp ?? minTimestamp,
						interpolationGap * 1000
					)
					const timeDiff = nextTimestamp - currentTimestamp

					if (nextTimestamp !== currentTimestamp) {
						portfolioData.push({
							timestamp: currentTimestamp,
							total: filteredPortfolioValues[index].total,
						})
					}
					if (timeDiff > interpolationGap * 1000) {
						const gapCount = Math.floor(timeDiff / (interpolationGap * 1000)) - 1
						for (let j = 1; j <= gapCount; j++) {
							portfolioData.push({
								timestamp: currentTimestamp + j * interpolationGap * 1000,
								total: filteredPortfolioValues[index].total,
							})
						}
					}
				}
			}

			portfolioData.push(portfolioValues[portfolioValues.length - 1])
			return portfolioData
		}

		const crossPortfolioData = createPortfolioData(crossMarginPnlValues)
		const smartPortfolioData = createPortfolioData(smartPortfolioValues)
		return {
			[FuturesMarginType.CROSS_MARGIN]: crossPortfolioData,
			[FuturesMarginType.SMART_MARGIN]: smartPortfolioData,
		}
	}
)

export const selectMaxUsdSizeInput = createSelector(
	selectPerpsProvider,
	selectMaxUsdSizeInputCrossMargin,
	selectMaxLeverage,
	selectMarginDeltaInputValue,
	(futuresType, maxCrossMarginInput, maxLeverage, marginDelta) => {
		return futuresType === PerpsProvider.SNX_V2
			? maxLeverage.mul(marginDelta || 0)
			: maxCrossMarginInput
	}
)

export const selectAvailableOi = createSelector(selectMarketInfo, (marketInfo) => {
	const availableOiUsdShort =
		marketInfo?.marketLimitUsd.sub(marketInfo.openInterest.shortUSD) ?? wei(0)

	const availableOiUsdLong =
		marketInfo?.marketLimitUsd.sub(marketInfo.openInterest.longUSD) ?? wei(0)

	const availableOiNativeShort =
		marketInfo?.marketLimitNative.sub(marketInfo.openInterest.short) ?? wei(0)

	const availableOiNativeLong =
		marketInfo?.marketLimitNative.sub(marketInfo.openInterest.long) ?? wei(0)

	return {
		short: {
			usd: availableOiUsdShort,
			native: availableOiNativeShort,
		},
		long: {
			usd: availableOiUsdLong,
			native: availableOiNativeLong,
		},
	}
})

export const selectMarketSuspended = createSelector(
	selectMarketInfo,
	(marketInfo) => marketInfo?.isSuspended
)

export const selectHistoricalFundingRatePeriod = (state: RootState) =>
	state.futures.preferences.historicalFundingRatePeriod

export const selectTradePanelSLTPValidity = createSelector(
	selectPerpsProvider,
	selectSlTpTradeInputs,
	selectSmartMarginTradePreview,
	selectTradePrice,
	selectMarketOnchainPrice,
	selectLeverageSide,
	(
		type,
		{ stopLossPrice, takeProfitPrice },
		smTradePreview,
		tradePrice,
		onChainPrice,
		leverageSide
	) => {
		if (type === PerpsProvider.SNX_V3) {
			// TODO: Currently cross margin has no liquidation price value in the trade preview to base a max stop loss off of
			const furthestPrice = leverageSide === PositionSide.LONG ? wei(0) : wei(tradePrice.mul(10))
			return {
				takeProfit: takeProfitValidity(takeProfitPrice, leverageSide, tradePrice),
				stopLoss: {
					invalidLabel: undefined,
					minMaxStopPrice: furthestPrice,
				},
			}
		}
		const tpValidity = takeProfitValidity(takeProfitPrice, leverageSide, onChainPrice, tradePrice)
		const slValidity = stopLossValidity(
			stopLossPrice,
			smTradePreview?.liqPrice || wei(0),
			leverageSide,
			tradePrice,
			onChainPrice
		)
		return {
			takeProfit: tpValidity,
			stopLoss: slValidity,
		}
	}
)

export const selectSlTpModalInputs = createSelector(
	(state: RootState) => state.futures.sltpModalInputs,
	(inputs) => ({
		stopLossPrice: inputs.stopLossPrice ?? '',
		takeProfitPrice: inputs.takeProfitPrice ?? '',
	})
)

export const selectModalSLTPValidity = createSelector(
	selectSlTpModalInputs,
	selectV2EditPositionModalInfo,
	selectV3EditPositionModalInfo,
	selectPerpsProvider,
	({ takeProfitPrice, stopLossPrice }, v2Info, v3Info, type) => {
		const { position, onChainPrice, marketPrice } = type === PerpsProvider.SNX_V3 ? v3Info : v2Info

		// Remove onchain price for V3.
		const tpValidity = takeProfitValidity(
			takeProfitPrice,
			position?.details?.side || PositionSide.LONG,
			marketPrice,
			type === PerpsProvider.SNX_V2 ? onChainPrice : undefined
		)
		const slValidity = stopLossValidity(
			stopLossPrice,
			position?.details?.liquidationPrice,
			position?.details?.side || PositionSide.LONG,
			marketPrice,
			type === PerpsProvider.SNX_V2 ? onChainPrice : undefined
		)

		return {
			takeProfit: tpValidity,
			stopLoss: slValidity,
		}
	}
)

export const selectPendingOrdersCount = createSelector(
	selectPendingAsyncOrdersCount,
	selectSmartMarginDelayedOrders,
	selectPerpsProvider,
	selectUserInfoShowAllMarkets,
	selectMarketAsset,
	(asyncCount, delayedOrders, type, userInfoShowAllMarkets, currentMarket) =>
		type === PerpsProvider.SNX_V3
			? asyncCount
			: userInfoShowAllMarkets
			? delayedOrders.length
			: delayedOrders.filter(({ market }) => market.asset === currentMarket).length
)

export const selectFuturesSignerSupportedNetwork = createSelector(
	selectSignerSupportsCrossMargin,
	selectWalletChainSupportsSmartMargin,
	selectPerpsProvider,
	(cmSupported, smSupported, type) => {
		return type === PerpsProvider.SNX_V3 ? cmSupported : smSupported
	}
)

export const selectLeaderboardTableData = createSelector(
	selectFuturesPositions,
	selectPositionHistoryForSelectedTrader,
	(positions, history) => formatPositionForTable(positions, history)
)

export const selectPositionsHistoryTableData = createSelector(
	selectFuturesPositions,
	selectUsersPositionHistory,
	(positions, history) => formatPositionForTable(positions, history)
)

export const selectCMTradesHistoryTableData = createSelector(
	selectMarketAsset,
	selectCrossMarginAccountTrades,
	selectCrossMarginAccountLiquidations,
	selectUserInfoShowAllMarkets,
	(state: RootState) => state.futures.preferences.userInfoTableFilter['trades'],
	(marketAsset, trades, liquidations, userInfoShowAllMarkets, tableFilter) => {
		const liquidationTrades = liquidations.map((liquidation) => {
			return {
				market: {
					asset: marketAsset,
					marketKey: liquidation.market.marketKey,
					marketName: liquidation.market.marketName,
				},
				pnl: null,
				feesPaid: null,
				funding: null,
				netPnl: null,
				side: null,
				notionalValue: null,
				price: liquidation.estimatedPrice,
				amount: liquidation.amount.abs(),
				timestamp: liquidation.timestamp * 1000,
				settlementTxHash: null,
				committedTxHash: null,
				displayAsset: getDisplayAsset(liquidation?.market?.asset),
				orderType: 'Liquidation',
			}
		})
		const normalTrades = trades.map((trade) => {
			const pnl = wei(trade.pnl)
			const feesPaid = trade.totalFees
			const funding = trade.accruedFunding

			return {
				market: {
					asset: marketAsset,
					marketKey: trade.market.marketKey,
					marketName: trade.market.marketName,
				},
				pnl,
				feesPaid,
				funding,
				netPnl: trade.pnlWithFeesPaid,
				side: trade.sizeDelta.lt(0) ? PositionSide.SHORT : PositionSide.LONG,
				notionalValue: trade?.fillPrice.mul(trade?.sizeDelta.abs()),
				price: trade.fillPrice,
				amount: trade.sizeDelta.abs(),
				timestamp: trade.timestamp * 1000,
				settlementTxHash: trade.settlementTxHash,
				committedTxHash: trade.commitmentTxHash,
				displayAsset: getDisplayAsset(trade?.market?.asset),
				interestCharged: trade.interestCharged,
				orderType: 'Market',
			}
		})
		return [...normalTrades, ...liquidationTrades]
			.filter((t) => t.market.asset === marketAsset || userInfoShowAllMarkets)
			.filter((t) => shouldFilterUserInfoTable(new Date(t.timestamp), tableFilter))
			.sort((a, b) => b.timestamp - a.timestamp)
	}
)

export const selectSMTradesHistoryTableData = createSelector(
	selectAllSmartMarginTrades,
	selectUserInfoShowAllMarkets,
	selectMarketAsset,
	(state: RootState) => state.futures.preferences.userInfoTableFilter['trades'],
	(history, userInfoShowAllMarkets, marketAsset, tableFilter) => {
		return history
			.map((trade) => {
				const pnl = trade.pnl
				const feesPaid = trade.feesPaid
				const funding = trade.fundingAccrued
				const netPnl = pnl.sub(wei(feesPaid)).sub(wei(funding))
				const notionalValue = trade?.price.mul(trade?.size.abs())

				return {
					side: trade.side,
					price: trade.price,
					market: {
						asset: marketAsset,
						marketKey: trade.market.marketKey,
						marketName: trade.market.marketName,
					},
					pnl,
					feesPaid,
					funding,
					netPnl,
					notionalValue,
					amount: trade.size.abs(),
					timestamp: trade.timestamp * 1000,
					settlementTxHash: trade.txnHash,
					committedTxHash: trade.commitmentTxHash,
					displayAsset: getDisplayAsset(trade?.asset),
					orderType: trade?.orderType,
				}
			})
			.filter((t) => t.market.asset === marketAsset || userInfoShowAllMarkets)
			.filter((t) => shouldFilterUserInfoTable(new Date(t.timestamp), tableFilter))
	}
)

export const selectTradesHistoryTableData = createSelector(
	selectPerpsProvider,
	selectCMTradesHistoryTableData,
	selectSMTradesHistoryTableData,
	(type, cmHistory, smHistory) => {
		return type === PerpsProvider.SNX_V3 ? cmHistory : smHistory
	}
)

export const selectSelectedDelegationAddress = (state: RootState) =>
	state.futures.delegation.selectedDelegationAddress as Address

export const selectNewNickname = (state: RootState) => state.futures.delegation.newNickname

export const selectAddressBook = createSelector(
	selectSignerWallet,
	(state: RootState) => state.futures.delegation.addressBook,
	(wallet, addressBook) => {
		if (!wallet) return []
		return addressBook[wallet] ?? []
	}
)

export const selectSessionInfo = (state: RootState) => state.futures.oneClickTrading.sessionInfo

export const selectSessionExpiry = (state: RootState) =>
	(!!state.futures.oneClickTrading.sessionInfo &&
		state.futures.oneClickTrading.sessionInfo.validUntil) ||
	0

export const selectAbstractionBalance = (state: RootState) =>
	state.futures.oneClickTrading.ethBalance

export const selectAbstractionDelegated = (state: RootState) =>
	state.futures.oneClickTrading.accountDelegated

export const selectAbstractionTxCost = (state: RootState) => state.futures.oneClickTrading.estTxCost

export const selectShowOneClickTradingOnboard = (state: RootState) =>
	state.app.showModal?.type === 'one_click_trading_onboard'

export const selectOneClickReady = createSelector(
	selectTradingMode,
	(mode) => mode === TradingModes.ONE_CLICK
)

export const selectDelegationTradingWarning = createSelector(
	selectTradingMode,
	selectOneClickReady,
	selectIsSubAccountMode,
	(mode, oneClickTradingReady, isSubaAcountMode) =>
		(oneClickTradingReady || mode === TradingModes.ONE_CLICK) && isSubaAcountMode
)

export const selectHistoricalFundingRate = createSelector(
	selectPerpsProvider,
	(state: RootState) => state,
	(type, state) =>
		type === PerpsProvider.SNX_V3
			? state.snxPerpsV3.historicalFundingRates
			: state.snxPerpsV2.historicalFundingRates
)

export const selectOneClickTradeBtnI18n = createSelector(
	selectOneClickTradingSelected,
	selectAbstractionDelegated,
	selectKeeperDepositExceedsAbstractionBal,
	(selected, isDelegated, exceedsAbstractionBal) => {
		if (!exceedsAbstractionBal && selected) {
			return isDelegated
				? 'futures.one-click-trading.start-session'
				: 'futures.one-click-trading.onboard.activate'
		}
	}
)

export const selectSessionActive = (state: RootState) =>
	state.futures.oneClickTrading.isSessionActive

export const selectSelectedChart = (state: RootState) => state.futures.preferences.selectedChart
export const selectManageModalType = (state: RootState) =>
	state.futures.preferences.selectedManageModalType

export const selectDelegatesForAccount = createSelector(
	selectPerpsProvider,
	selectCrossMarginAccountDataBySignerWallet,
	selectSmartMarginAccountDataBySignerWallet,
	(perpsProvider, cmAccountData, smAccountData) =>
		perpsProvider === PerpsProvider.SNX_V3
			? cmAccountData?.delegates ?? []
			: smAccountData?.delegates ?? []
)

export const selectSubAccountsForAccount = createSelector(
	selectPerpsProvider,
	selectCrossMarginAccountDataBySignerWallet,
	selectSmartMarginAccountDataBySignerWallet,
	(perpsProvider, cmAccountData, smAccountData) =>
		perpsProvider === PerpsProvider.SNX_V3
			? cmAccountData?.subAccounts ?? []
			: smAccountData?.subAccounts ?? []
)

export const selectKeeperDeposit = createSelector(
	selectSmartMarginKeeperDeposit,
	selectTradePanelKeeperDepositV3,
	selectPerpsProvider,
	(keeperDeposit, crossMarginDeposit, perpsProvider) => {
		return perpsProvider === PerpsProvider.SNX_V3 ? crossMarginDeposit : keeperDeposit
	}
)

export const selectCloseModalKeeperDeposit = createSelector(
	selectCloseModalKeeperDepositV3,
	selectSmartMarginKeeperDeposit,
	selectPerpsProvider,
	(closeModalKeeperDeposit, smartMarginKeeperDeposit, futuresType) => {
		return futuresType === PerpsProvider.SNX_V3 ? closeModalKeeperDeposit : smartMarginKeeperDeposit
	}
)

export const selectKeeperDepositExceedsBal = createSelector(
	selectPerpsProvider,
	selectKeeperUsdDepositExceedsBal,
	selectKeeperDeposit,
	selectEthWalletBalance,
	selectOneClickTradingSelected,
	selectKeeperDepositExceedsAbstractionBal,
	(type, v3ExceedsBalance, deposit, walletEthBalance, oneClickSelected, exceedsAbstractionBal) => {
		if (type === PerpsProvider.SNX_V3) {
			return v3ExceedsBalance
		}
		if (oneClickSelected && !exceedsAbstractionBal) {
			// There is enough in the abstraction wallet to deposit to smart margin
			return false
		}
		return deposit.gt(walletEthBalance)
	}
)

export const selectSLTPModalEthDeposit = createSelector(
	selectSLTPModalUsdDepositV3,
	selectSmartMarginKeeperDeposit,
	selectPerpsProvider,
	(crossMarginDeposit, smartMarginDeposit, futuresType) => {
		return futuresType === PerpsProvider.SNX_V3 ? crossMarginDeposit : smartMarginDeposit
	}
)

export const selectIsFetchingTradePreview = createSelector(
	selectIsFetchingCMTradePreview,
	selectIsFetchingTradePreviewSmartMargin,
	selectPerpsProvider,
	(cmPreview, smPreview, type) => {
		return type === PerpsProvider.SNX_V3 ? cmPreview : smPreview
	}
)

export const selectEditPositionModalInfo = createSelector(
	selectPerpsProvider,
	selectV2EditPositionModalInfo,
	selectV3EditPositionModalInfo,
	(type, v2Info, v3Info) => {
		return type === PerpsProvider.SNX_V3 ? v3Info : v2Info
	}
)

export const selectClosePositionPreview = createSelector(
	selectPerpsProvider,
	selectCloseCMPositionPreview,
	selectCloseSMPositionPreview,
	(type, cmInputs, smInputs) => {
		return type === PerpsProvider.SNX_V3 ? cmInputs : smInputs
	}
)

export const selectEditPositionPreview = createSelector(
	selectCMEditPreviewUnserialized,
	selectSMEditPreviewUnserialized,
	selectEditPositionInputs,
	selectPerpsProvider,
	(crossMarginPreview, smartMarginPreview, { nativeSizeDelta }, type) => {
		if (isNaN(Number(nativeSizeDelta))) return null
		const preview = type === PerpsProvider.SNX_V3 ? crossMarginPreview : smartMarginPreview
		// @ts-ignore no margin on v3
		const margin = preview?.margin
		if (preview) {
			const priceImpact = getDefaultPriceImpact('market')
			const desiredFillPrice = calculateDesiredFillPrice(
				wei(nativeSizeDelta || 0),
				preview.fillPrice,
				priceImpact
			)

			return {
				...preview,
				desiredFillPrice,
				leverage: margin?.gt(0) ? preview.notionalValue.div(margin).abs().toNumber() : 0,
			}
		}
		return null
	}
)

export const selectConditionalOrderTableItems = createSelector(
	selectPerpsProvider,
	selectUserInfoShowAllMarkets,
	selectAllSmartMarginConditionalOrders,
	selectCrossMarginOrderTableItems,
	selectMarketAsset,
	(type, userInfoShowAllMarkets, smOrders, cmOrders, currentMarket) => {
		return type === PerpsProvider.SNX_V3
			? userInfoShowAllMarkets
				? cmOrders
				: cmOrders.filter((o) => o.asset === currentMarket)
			: userInfoShowAllMarkets
			? smOrders
			: smOrders.filter((o) => o.asset === currentMarket)
	}
)

export const selectConditionalOrdersCount = createSelector(
	selectConditionalOrderTableItems,
	(items) => items.length
)

export const selectShowSwitchNetwork = createSelector(
	selectShowSwitchNetworkCrossMargin,
	selectShowSwitchNetworkSmartMargin,
	selectPerpsProvider,
	(switchCM, switchSM, type) => {
		return type === PerpsProvider.SNX_V3 ? switchCM : switchSM
	}
)

export const selectProfitCalculatorVisible = (state: RootState) =>
	state.futures.preferences.profitCalculatorVisible

export const selectFavoriteMarkets = (state: RootState) => state.futures.preferences.favoriteMarkets

export const selectUserInfoTableFilter =
	(futuresTab: TableFilteredFuturesTabs) => (state: RootState) =>
		state.futures.preferences.userInfoTableFilter[futuresTab]

export const selectKeeperFeeAsset = createSelector(selectPerpsProvider, (type) => {
	return type === PerpsProvider.SNX_V3 ? 'USDC' : 'ETH'
})

export const selectIsDraggingSlider = (state: RootState) =>
	state.futures.tradePanel.isDraggingSlider

export const selectAccountTransfers = createSelector(
	selectPerpsProvider,
	selectSmartMarginAccountTransfers,
	selectCrossMarginAccountTransfers,
	(type, smTransfers, cmTransfers) => (type === PerpsProvider.SNX_V3 ? cmTransfers : smTransfers)
)

export const selectAccountOrderHistory = createSelector(
	selectPerpsProvider,
	selectSmartMarginAccountOrderHistory,
	selectCrossMarginAccountOrderHistory,
	(type, smOrders, cmOrders) => {
		return type === PerpsProvider.SNX_V3 ? cmOrders : smOrders
	}
)

export const selectConditionalOrdersForMarket = createSelector(
	selectPerpsProvider,
	selectConditionalOrdersForV2Market,
	selectCrossMarginOrderTableItems,
	(type, v2Orders, v3Orders) => {
		return type === PerpsProvider.SNX_V3 ? v3Orders : v2Orders
	}
)

export const selectDashboardHistoryTableFilter = (historyTab: HistoryTab) => (state: RootState) =>
	state.futures.preferences.dashboardHistoryTableFilter[historyTab]

export const selectPositionsHistoryFilteredTableData = createSelector(
	selectPositionsHistoryTableData,
	(state: RootState) => state.futures.preferences.dashboardHistoryTableFilter['positions'],
	(positions, tableFilter) => {
		return positions
			.filter((p) => shouldFilterHistoryTableByDate(new Date(p.timestamp), tableFilter))
			.filter((p) =>
				shouldFilterHistoryTable({ asset: p.asset, side: p.side, status: p.status }, tableFilter)
			)
	}
)

export const selectTradesHistoryFilteredTableData = createSelector(
	selectTradesHistoryTableData,
	(state: RootState) => state.futures.preferences.dashboardHistoryTableFilter['trades'],
	(trades, tableFilter) => {
		return trades
			.filter((t) => shouldFilterHistoryTableByDate(new Date(t.timestamp), tableFilter))
			.filter((t) =>
				shouldFilterHistoryTable(
					{
						asset: t.displayAsset || '',
						side: t.side ?? undefined,
						settlementTxHash: t.settlementTxHash ?? undefined,
						committedTxHash: t.committedTxHash ?? undefined,
					},
					tableFilter
				)
			)
	}
)

export const selectAccountOrderFilteredHistory = createSelector(
	selectAccountOrderHistory,
	(state: RootState) => state.futures.preferences.userInfoTableFilter?.['order_history'],
	(state: RootState) => state.futures.preferences.dashboardHistoryTableFilter?.['order_history'],
	(orders, userInfoTableFilter, dashboardTableFilter) => {
		const pathName = window.location.pathname
		return pathName.includes('market')
			? orders
					.filter((o) => shouldFilterUserInfoTable(new Date(o.timestamp), userInfoTableFilter))
					.filter(
						(o) =>
							!userInfoTableFilter?.type ||
							userInfoTableFilter?.type === 'all_orders' ||
							toSnakeCase(o.orderType) === userInfoTableFilter?.type
					)
			: orders
					.filter((o) =>
						shouldFilterHistoryTableByDate(new Date(o.timestamp), dashboardTableFilter)
					)
					.filter((o) =>
						shouldFilterHistoryTable(
							{
								asset: o.displayAsset || '',
								type: o.orderType,
								side: o.side,
								status: o.status,
								settlementTxHash: o.txnHash || '',
							},
							dashboardTableFilter
						)
					)
	}
)

export const selectAccountFilteredTransfers = createSelector(
	selectAccountTransfers,
	(state: RootState) => state.futures.preferences.userInfoTableFilter?.['transaction_history'],
	(state: RootState) =>
		state.futures.preferences.dashboardHistoryTableFilter?.['transaction_history'],
	(transfers, userInfoTableFilter, dashboardTableFilter) => {
		const pathName = window.location.pathname
		return pathName.includes('market')
			? transfers
					.filter((tx) => shouldFilterUserInfoTable(new Date(tx.timestamp), userInfoTableFilter))
					.filter(
						(tx) =>
							!userInfoTableFilter?.type ||
							userInfoTableFilter?.type === 'all_transactions' ||
							toSnakeCase(tx.action) === userInfoTableFilter?.type
					)
			: transfers
					.filter((tx) =>
						shouldFilterHistoryTableByDate(new Date(tx.timestamp), dashboardTableFilter)
					)
					.filter((tx) =>
						shouldFilterHistoryTable(
							{ asset: tx.asset, settlementTxHash: tx.txHash, type: tx.action },
							dashboardTableFilter
						)
					)
	}
)

export const selectUserInfoOrderHistory = createSelector(
	selectAccountOrderFilteredHistory,
	selectUserInfoShowAllMarkets,
	selectMarketAsset,
	(orderHistory, userInfoShowAllMarkets, currentMarket) => {
		if (userInfoShowAllMarkets) {
			return orderHistory
		}
		return orderHistory.filter((order) => order.displayAsset === getDisplayAsset(currentMarket))
	}
)

export const selectUserInfoTradeHistory = createSelector(
	selectTradesHistoryTableData,
	selectUserInfoShowAllMarkets,
	selectMarketAsset,
	(tradeHistory, userInfoShowAllMarkets, currentMarket) => {
		if (userInfoShowAllMarkets) {
			return tradeHistory
		}
		return tradeHistory.filter((trade) => trade.displayAsset === getDisplayAsset(currentMarket))
	}
)

export const selectMarginAccount = createSelector(
	selectPerpsProvider,
	selectCrossMarginAccount,
	selectSmartMarginAccount,
	(provider, cmAccount, smAccount) => {
		return provider === PerpsProvider.SNX_V3 ? cmAccount : smAccount
	}
)

export const selectPerpsInterestRate = createSelector(
	selectV3SelectedMarketInfo,
	selectPerpsProvider,
	(marketInfo, type) => {
		const rate = type === PerpsProvider.SNX_V3 ? marketInfo?.settings.interestRate : undefined
		const rateWei = wei(rate ?? 0)
		return {
			yearly: rateWei,
			daily: rateWei.gt(0) ? rateWei.div(365) : wei(0),
			hourly: rateWei.gt(0) ? rateWei.div(365 * 24) : wei(0),
		}
	}
)
export const selectLoadingGloablHistory = createSelector(selectQueryStatuses, (statuses) => {
	return statuses.globalTradesHistory?.status === FetchStatus.Loading
})
